import { useEffect, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

/**
 * ApartmentSync paints Interactive Map / Floor Plans / List View as a floated
 * tab strip under HeroSolidBG's centered chevron. Vendor widths are often
 * uneven (Interactive Map runs wide), so the V tip lands on the Map|Plans
 * seam instead of the Floor Plans heading.
 *
 * Once the widget mounts, find those three headings by label and force an
 * equal three-column row so Floor Plans centers under the chevron.
 */
const VIEW_TAB_LABELS: Array<{ key: string; re: RegExp }> = [
  { key: 'map', re: /^interactive\s*map$/i },
  { key: 'floorplans', re: /^floor\s*plans$/i },
  { key: 'list', re: /^list\s*view$/i },
];

function normalizeLabel(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function matchViewTab(text: string): string | null {
  const label = normalizeLabel(text);
  return VIEW_TAB_LABELS.find((tab) => tab.re.test(label))?.key ?? null;
}

function findViewTabElements(root: ParentNode): HTMLElement[] {
  const candidates = root.querySelectorAll('a, button, li, div, span, label, td, th');
  const deepest: HTMLElement[] = [];

  for (const node of candidates) {
    if (!(node instanceof HTMLElement)) continue;
    if (!matchViewTab(node.textContent || '')) continue;
    // Skip large wrappers; keep the leaf-ish heading control.
    if (node.querySelectorAll('*').length > 12) continue;
    const hasDeeperMatch = Array.from(
      node.querySelectorAll('a, button, li, div, span, label, td, th'),
    ).some((child) => {
      if (!(child instanceof HTMLElement) || child === node) return false;
      if (child.querySelectorAll('*').length > 12) return false;
      return Boolean(matchViewTab(child.textContent || ''));
    });
    if (hasDeeperMatch) continue;
    deepest.push(node);
  }

  const byKey = new Map<string, HTMLElement>();
  for (const el of deepest) {
    const key = matchViewTab(el.textContent || '');
    if (key && !byKey.has(key)) byKey.set(key, el);
  }

  return VIEW_TAB_LABELS.map(({ key }) => byKey.get(key)).filter(
    (el): el is HTMLElement => Boolean(el),
  );
}

function sharedTabRow(tabs: HTMLElement[]): HTMLElement | null {
  if (tabs.length < 2) return null;

  let parent = tabs[0]?.parentElement ?? null;
  while (parent) {
    if (tabs.every((tab) => parent!.contains(tab))) {
      const depths = tabs.map((tab) => {
        let depth = 0;
        let node: HTMLElement | null = tab;
        while (node && node !== parent) {
          depth += 1;
          node = node.parentElement;
        }
        return depth;
      });
      // Prefer the nearest common ancestor (direct or one wrapper deep).
      if (Math.max(...depths) <= 3) return parent;
    }
    parent = parent.parentElement;
  }

  return tabs[0]?.parentElement ?? null;
}

function equalizeViewTabs(section: HTMLElement): void {
  const tabs = findViewTabElements(section);
  if (tabs.length < 3) return;

  const row = sharedTabRow(tabs);
  if (!row) return;

  row.classList.add('as-view-tabs-equalized');
  row.style.setProperty('display', 'flex', 'important');
  row.style.setProperty('flex-wrap', 'nowrap', 'important');
  row.style.setProperty('width', '100%', 'important');
  row.style.setProperty('align-items', 'stretch', 'important');
  row.style.setProperty('clear', 'both', 'important');

  // Style each tab's flex item — the matched node or its direct row child.
  for (const tab of tabs) {
    let item: HTMLElement = tab;
    while (item.parentElement && item.parentElement !== row) {
      item = item.parentElement;
    }
    item.style.setProperty('float', 'none', 'important');
    item.style.setProperty('flex', '1 1 0', 'important');
    item.style.setProperty('width', 'auto', 'important');
    item.style.setProperty('max-width', 'none', 'important');
    item.style.setProperty('box-sizing', 'border-box', 'important');
    item.style.setProperty('text-align', 'center', 'important');
  }
}

export function ApartmentSyncSection() {
  const [mapArmed, setMapArmed] = useState(false);
  const blockSlot = useBlockSlot('apartmentsyncsection.block', {
    defaultValue: 'apartment-sync',
  });

  useEffect(() => {
    const section = document.querySelector('.apartment-sync-section') as HTMLElement | null;
    if (!section) return;

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => equalizeViewTabs(section));
    };

    schedule();

    const observer = new MutationObserver(schedule);
    observer.observe(section, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [blockSlot?.sectionId]);

  return (
    <section
      className={`apartment-sync-section${mapArmed ? '' : ' map-scroll-block'}`}
      onMouseLeave={() => setMapArmed(false)}
    >
      <EditableToken path="apartmentsyncsection.block" as="div">
        {blockSlot ? (
          <SectionContextProvider sectionId={blockSlot.sectionId}>
            <blockSlot.BlockComponent sectionId={blockSlot.sectionId} />
          </SectionContextProvider>
        ) : (
          <div className="apartment-sync-empty">No block selected</div>
        )}
      </EditableToken>
      {!mapArmed && (
        <button
          type="button"
          className="apartment-sync-arm"
          aria-label="Click to interact with the map"
          onClick={() => setMapArmed(true)}
        />
      )}
      <style>{`
        .apartment-sync-section {
          position: relative;
          width: 100%;
          /* Clear the HeroSolidBG chevron so tabs/filters aren't flush with the V tip.
             Match page background so the gap reads as white space, not a dark band. */
          padding-top: clamp(2.5rem, 5vw, 3.5rem);
          background: var(--vibe-color-background, #fff);
        }
        .apartment-sync-empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }
        .apartment-sync-arm {
          position: absolute;
          inset: 0;
          z-index: 10;
          border: 0;
          background: transparent;
          cursor: pointer;
        }
        .map-scroll-block iframe { pointer-events: none !important; }

        /* Fallback when RRAC uses .rrac_tabSection floats — equal thirds so
           Floor Plans (middle) sits under the hero chevron tip. */
        .apartment-sync-section .rrac_tabSection {
          display: flex !important;
          flex-wrap: nowrap !important;
          width: 100% !important;
          clear: both !important;
        }
        .apartment-sync-section .rrac_tabSection::after {
          display: none !important;
        }
        .apartment-sync-section .rrac_tabSection > * {
          float: none !important;
          flex: 1 1 0 !important;
          width: auto !important;
          max-width: none !important;
          box-sizing: border-box !important;
          text-align: center !important;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSyncSection',
  displayName: 'Apartment Sync Section',
  tokens: {
    'apartmentsyncsection.block': {
      label: 'Block',
      type: 'block',
      defaultValue: 'apartment-sync',
      acceptedBlockCategories: ['Integrations'],
    },
  },
};

(ApartmentSyncSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ApartmentSyncSection;
